import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../lib/api';
import { normalizeRole } from '../../lib/roles';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

/**
 * EL PROFESIONAL de una agenda (30/09/2026): un usuario del equipo o, si aún
 * no tiene usuario, su nombre escrito (la agenda funciona igual: la ven y
 * citan en ella administración, recepción y enfermería). Cuando le den
 * usuario, se elige aquí en Configurar agenda.
 *
 * Los usuarios los da GET /api/users (solo administradores, como esta
 * pantalla). Primero los médicos; después el resto del equipo, por si una
 * agenda es de alguien con otro rol. Las tablets, nunca. El servidor vuelve a
 * comprobar que el usuario existe y está activo, y pone su nombre.
 *
 * Props:
 *  - prefijo:  prefijo de ids y data-testid ('agenda-config', 'agenda-nueva')
 *  - userId:   '' = sin usuario
 *  - nombre:   el nombre escrito (o el del usuario elegido)
 *  - onChange: ({ userId, nombre }) => void
 *  - ayuda:    texto bajo el campo
 */
const ROLES_TABLET = ['tablet', 'tablet_consent'];
const porNombre = (a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'es');

export default function ProfesionalAgenda({ prefijo, userId, nombre, onChange, ayuda }) {
  const [usuarios, setUsuarios] = useState([]);
  const [estado, setEstado] = useState('cargando'); // 'cargando' | 'ok' | 'error'

  useEffect(() => {
    let vivo = true;
    api.get('/api/users')
      .then((r) => {
        if (!vivo) return;
        setUsuarios(Array.isArray(r?.data) ? r.data : []);
        setEstado('ok');
      })
      .catch(() => { if (vivo) setEstado('error'); });
    return () => { vivo = false; };
  }, []);

  const { medicos, resto } = useMemo(() => {
    const equipo = usuarios.filter((u) => u?.id && u.active !== false
      && !ROLES_TABLET.includes(normalizeRole(u.role)));
    return {
      medicos: equipo.filter((u) => normalizeRole(u.role) === 'doctor').sort(porNombre),
      resto: equipo.filter((u) => normalizeRole(u.role) !== 'doctor').sort(porNombre),
    };
  }, [usuarios]);
  const fueraDeLista = Boolean(userId) && ![...medicos, ...resto].some((u) => u.id === userId);

  const elegir = (id) => {
    if (!id) {
      // Sin usuario: se queda escrito el nombre que había, para corregirlo.
      onChange({ userId: '', nombre });
      return;
    }
    const u = [...medicos, ...resto].find((x) => x.id === id);
    onChange({ userId: id, nombre: u?.name || nombre });
  };

  return (
    <div data-testid={`${prefijo}-profesional`}>
      <Label htmlFor={`${prefijo}-profesional-usuario`}>Profesional</Label>
      <select
        id={`${prefijo}-profesional-usuario`}
        value={userId}
        onChange={(e) => elegir(e.target.value)}
        className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm"
        data-testid={`${prefijo}-profesional-usuario`}
      >
        <option value="">Aún no tiene usuario: escribir su nombre</option>
        {medicos.length > 0 && (
          <optgroup label="Médicos">
            {medicos.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </optgroup>
        )}
        {resto.length > 0 && (
          <optgroup label="Resto del equipo">
            {resto.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </optgroup>
        )}
        {fueraDeLista && (
          <option value={userId}>
            {nombre || userId}{estado === 'ok' ? ' (su usuario ya no está activo)' : ''}
          </option>
        )}
      </select>
      {!userId && (
        <Input
          value={nombre}
          onChange={(e) => onChange({ userId: '', nombre: e.target.value })}
          placeholder="Nombre del profesional (p. ej. Dra. Nombre Apellido)"
          maxLength={120}
          className="mt-2"
          aria-label="Nombre del profesional"
          data-testid={`${prefijo}-profesional-nombre`}
        />
      )}
      {ayuda && <p className="text-xs text-muted-foreground mt-1">{ayuda}</p>}
    </div>
  );
}
